import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { db } from '@vibe/db/server';
import { AdAgenciesBoard } from '@api/BoardSDK';
import { executeIntegration } from '@api/integration-block.server';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';
import { agencyLetters, agencySends, agencyContacts } from '@generated/models';

const GMAIL = 'c70cb228-a141-49d4-a586-364172111780';
const GEORGE = 'gbrook8344@gmail.com';
const PORTAL = 'https://bit.ly/star-revue';
const MEDIA_KIT_PDF = 'https://files-public.monday.com/use1/c2888573-f8da-4b81-9c93-fd6e3fef0a8f/Star-Revue-Media-Kit.pdf';

const LETTER_1 = {
  subject: 'Here is our 2027 Media Kit and DIY ad portal',
  body: `Hi {first},

I'm George Fiala, publisher of the Red Hook Star-Revue (Brooklyn) and The Village Star-Revue (Manhattan) - two award-winning monthly community newspapers read cover to cover by engaged local audiences.

We've just launched a self-service portal where your team can see rates, sizes and specials, book space and upload camera-ready art in minutes. Visit the portal here:
${PORTAL}

Download our brand new media kit (PDF):
${MEDIA_KIT_PDF}

All rates are gross: rates are commissionable to recognized agencies. We publish 4,000 copies of each paper monthly. All print ads include full digital.

Please let me know if I can send one of our sales reps to show you our product in person.

As you can see from our media kit - we are very engaged with our readers in downtown Brooklyn and the East and West Village. We are told that for anyone looking to reach these audiences, we are the number one choice.

And thanks for opening my email! I look forward to doing business together!

Best,
George Fiala
Publisher, Red Hook Star-Revue & The Village Star-Revue
${GEORGE}
917 652-9128`,
};

const fill = (body: string, contact: string | null | undefined) => body.replace(/\{first\}/g, contact?.trim() ? contact.trim().split(/\s+/)[0] : 'there');
const nyDate = () => new Date(new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) + 'T12:00:00Z');
// Gmail rejects some characters (curly quotes, dashes, ellipses, brackets) as "invalid field values" — clean them, then retry plainer.
const ascii = (s: string) => s.normalize('NFKD').replace(/[\u2018\u2019]/g, "'").replace(/[\u201c\u201d]/g, '"').replace(/[\u2013\u2014]/g, '-').replace(/\u2026/g, '...').replace(/\u00b7/g, '-').replace(/[^\x09\x0a\x0d\x20-\x7e]/g, '');
async function mail(to: string, subject: string, body: string) {
  const subj = ascii(subject).replace(/[[\]{}<>]/g, '').trim();
  let lastErr: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await new Promise((r) => setTimeout(r, 4000 * attempt)); // Gmail sometimes refuses quick back-to-back sends
    try {
      return await withBoardAuth(() => executeIntegration(GMAIL, { to: [to.trim()], subject: attempt < 2 ? subj : subj.replace(/[^A-Za-z0-9 ,.'-]/g, ''), body: ascii(body) }));
    } catch (e) {
      console.error('gmail attempt', attempt, e); lastErr = e;
      if ((e as { code?: string })?.code === 'AI_CREDITS_EXHAUSTED') throw new Error('CREDITS: Your monday AI credits have run out for now, so emails from the app cannot be sent.');
    }
  }
  throw lastErr;
}

async function allLetters() {
  const col = db.collection(agencyLetters);
  let docs = await col.orderBy('number', 'asc').limit(100).get();
  if (!docs.length) {
    const now = new Date().toISOString();
    await col.add({ number: 1, ...LETTER_1, createdAt: now, updatedAt: now });
    docs = await col.orderBy('number', 'asc').limit(100).get();
  }
  return docs;
}
async function letterByNumber(n: number) {
  const l = (await allLetters()).find((d) => d.data.number === n);
  if (!l) throw new Error('Letter not found');
  return l.data;
}

export const getLetters = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const docs = await allLetters();
    const sends = await db.collection(agencySends).limit(1000).get();
    const extras = await db.collection(agencyContacts).limit(1000).get();
    return {
      contacts: extras.map((c) => ({ id: c.id, agencyId: c.data.agencyId, name: c.data.name, title: c.data.title, email: c.data.email, emailKind: c.data.emailKind })),
      letters: docs.map((d) => ({ id: d.id, ...d.data })),
      sends: sends.map((s) => ({ agencyId: s.data.agencyId, letterNumber: s.data.letterNumber, to: s.data.to, sentAt: s.data.sentAt, status: s.data.status ?? 'sent' })),
    };
  });

export const saveLetter = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), id: z.string().optional(), subject: z.string().min(1).max(300), body: z.string().min(1).max(10000) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const col = db.collection(agencyLetters);
    const now = new Date().toISOString();
    if (data.id) { await col.doc(data.id).update({ subject: data.subject, body: data.body, updatedAt: now }); return { ok: true }; }
    const docs = await allLetters();
    const number = Math.max(0, ...docs.map((d) => d.data.number)) + 1;
    await col.add({ number, subject: data.subject, body: data.body, createdAt: now, updatedAt: now });
    return { ok: true, number };
  });

/** Sends a letter to George only, marked [SAMPLE]. Records nothing. */
export const sendSampleLetter = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), subject: z.string().min(1).max(300), body: z.string().min(1).max(10000) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    await mail(GEORGE, `SAMPLE - ${data.subject}`, fill(data.body, 'George'));
    return { ok: true, to: GEORGE };
  });

/** Sends one letter to one agency (body already personalized in the dialog). */
export const sendLetterToAgency = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), agencyId: z.string().regex(/^\d+$/), letterNumber: z.number().int().min(1), to: z.string().min(3).max(200), subject: z.string().min(1).max(300), body: z.string().min(1).max(10000) }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    await mail(data.to, data.subject, data.body);
    await db.collection(agencySends).add({ agencyId: data.agencyId, letterNumber: data.letterNumber, to: data.to, sentAt: new Date().toISOString(), status: 'sent' });
    await withBoardAuth(() => new AdAgenciesBoard().item(data.agencyId).update({ outreachStatus: 'Media Kit Sent', lastContacted: nyDate() }).execute());
    return { ok: true };
  });

/** Sends a letter to every agency with an email that hasn't had it yet. Skips Not Interested / Bad Email / Booked Ad. */
export const sendLetterToAll = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string(), letterNumber: z.number().int().min(1), agencyId: z.string().regex(/^\d+$/).optional() }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const letter = await letterByNumber(data.letterNumber);
    const sentDocs = await db.collection(agencySends).where('letterNumber', '==', data.letterNumber).limit(1000).get();
    const already = new Set(sentDocs.filter((s) => (s.data.status ?? 'sent') === 'sent').map((s) => s.data.to.trim().toLowerCase()));
    const failedDocs = sentDocs.filter((s) => s.data.status === 'failed');
    const extras = await db.collection(agencyContacts).limit(1000).get();
    const b = new AdAgenciesBoard();
    const agencies: { id: string; contactName: string | null; email: { email: string } | null; outreachStatus: string | null }[] = [];
    let cursor: string | null | undefined;
    do {
      const r = await withBoardAuth(() => cursor
        ? b.items().withColumns(['contactName', 'email', 'outreachStatus']).withPagination({ limit: 100, cursor: cursor as string }).execute()
        : b.items().withColumns(['contactName', 'email', 'outreachStatus']).withPagination({ limit: 100 }).execute());
      agencies.push(...(r.items as unknown as typeof agencies));
      cursor = r.cursor;
    } while (cursor);
    let sent = 0, noEmail = 0, skipped = 0, failed = 0;
    for (const a of agencies) {
      if (data.agencyId && a.id !== data.agencyId) continue;
      if (['Not Interested', 'Bad Email', 'Booked Ad'].includes(a.outreachStatus ?? '')) { skipped++; continue; }
      const people: { to: string; name: string | null }[] = [];
      const main = a.email?.email?.trim();
      if (main && main.includes('@')) people.push({ to: main, name: a.contactName });
      for (const x of extras) if (x.data.agencyId === a.id && x.data.email.includes('@')) people.push({ to: x.data.email.trim(), name: x.data.name });
      if (!people.length) { noEmail++; continue; }
      let any = false;
      for (const p of people) {
        const key = p.to.toLowerCase();
        if (already.has(key)) { skipped++; continue; }
        already.add(key);
        try {
          await mail(p.to, letter.subject, fill(letter.body, p.name));
          await db.collection(agencySends).add({ agencyId: a.id, letterNumber: data.letterNumber, to: p.to, sentAt: new Date().toISOString(), status: 'sent' });
          for (const f of failedDocs.filter((f) => f.data.to.toLowerCase() === key)) await db.collection(agencySends).doc(f.id).delete();
          sent++; any = true;
          await new Promise((r) => setTimeout(r, 1500)); // space out sends so Gmail doesn't refuse them
        } catch (e) {
          if (e instanceof Error && e.message.startsWith('CREDITS:')) throw e;
          console.error('send failed', a.id, p.to, e); failed++;
          if (!failedDocs.some((f) => f.data.to.toLowerCase() === key)) await db.collection(agencySends).add({ agencyId: a.id, letterNumber: data.letterNumber, to: p.to, sentAt: new Date().toISOString(), status: 'failed' });
        }
      }
      if (any) await withBoardAuth(() => b.item(a.id).update({ outreachStatus: a.outreachStatus === 'Replied' ? 'Replied' : 'Media Kit Sent', lastContacted: nyDate() }).execute());
    }
    return { sent, noEmail, skipped, failed };
  });
